import * as Sentry from "@sentry/react";
import { getApiClient } from "@src/api";
import {
	invalidateNavbarBranchSnapshots,
	optimisticallyUpdateNavbarItemIcon,
	restoreNavbarBranchSnapshots,
} from "@src/components/board/mutations/navbar-optimistic";
import { graphql } from "@src/gql/gql";
import type { IconInput, NavbarSection } from "@src/gql/graphql";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const setNavbarItemIconMutation = graphql(/* GraphQL */ `
	mutation setNavbarItemIcon(
		$section: NavbarSection!
		$itemKey: String!
		$icon: IconInput!
		$waitForProjections: Boolean
	) {
		navbar {
			setItemIcon(
				section: $section
				itemKey: $itemKey
				icon: $icon
				waitForProjections: $waitForProjections
			) {
				id
				key
				name
				type
				icon {
					type
					value
				}
			}
		}
	}
`);

const removeNavbarItemIconMutation = graphql(/* GraphQL */ `
	mutation removeNavbarItemIcon(
		$section: NavbarSection!
		$itemKey: String!
		$waitForProjections: Boolean
	) {
		navbar {
			removeItemIcon(
				section: $section
				itemKey: $itemKey
				waitForProjections: $waitForProjections
			) {
				id
				key
				name
				type
				icon {
					type
					value
				}
			}
		}
	}
`);

export type SetNavbarItemIconInput = {
	section: NavbarSection;
	itemKey: string;
	icon: IconInput;
	waitForProjections?: boolean;
};

export type RemoveNavbarItemIconInput = {
	section: NavbarSection;
	itemKey: string;
	waitForProjections?: boolean;
};

export function useSetNavbarItemIcon() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			section,
			itemKey,
			icon,
			waitForProjections,
		}: SetNavbarItemIconInput) => {
			const response = await client.mutate({
				mutation: setNavbarItemIconMutation,
				variables: {
					section,
					itemKey,
					icon,
					waitForProjections: waitForProjections ?? false,
				},
			});
			return response.data?.navbar?.setItemIcon ?? null;
		},
		onMutate: async ({ itemKey, icon }) => {
			const snapshots = await optimisticallyUpdateNavbarItemIcon(
				queryClient,
				itemKey,
				{
					type: icon.type,
					value: icon.value,
				},
			);
			return { snapshots };
		},
		onError: (error, _variables, context) => {
			if (context?.snapshots) {
				restoreNavbarBranchSnapshots(queryClient, context.snapshots);
			}
			Sentry.captureException(error);
		},
		onSettled: (_data, _error, _variables, context) => {
			if (context?.snapshots) {
				invalidateNavbarBranchSnapshots(queryClient, context.snapshots);
			}
		},
	});
}

export function useRemoveNavbarItemIcon() {
	const client = getApiClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({
			section,
			itemKey,
			waitForProjections,
		}: RemoveNavbarItemIconInput) => {
			const response = await client.mutate({
				mutation: removeNavbarItemIconMutation,
				variables: {
					section,
					itemKey,
					waitForProjections: waitForProjections ?? false,
				},
			});
			return response.data?.navbar?.removeItemIcon ?? null;
		},
		onMutate: async ({ itemKey }) => {
			const snapshots = await optimisticallyUpdateNavbarItemIcon(
				queryClient,
				itemKey,
				null,
			);
			return { snapshots };
		},
		onError: (error, _variables, context) => {
			if (context?.snapshots) {
				restoreNavbarBranchSnapshots(queryClient, context.snapshots);
			}
			Sentry.captureException(error);
		},
		onSettled: (_data, _error, _variables, context) => {
			if (context?.snapshots) {
				invalidateNavbarBranchSnapshots(queryClient, context.snapshots);
			}
		},
	});
}
